import React, { useState } from 'react';
import { X, Plus } from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Badge } from './ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { OutsourcedShow } from '../types/OutsourcedShow';
import { CharacterGender } from '../constants/constants';

export interface ShowTransferData {
  showName: string;
  description: string;
  actorEthnicity: string;
  targetAudienceGender: string;
  maturityRating: string;
  sourceLanguage: string;
  subGenres: string[];
  subTropes: string[];
  tags: string[];
}

export interface TransferShowAdditionalFields {
  showName: string;
  actorEthnicity?: string;
  targetAudienceGender?: string;
  maturityRating?: string;
  sourceLanguage?: string;
  subTropes: string[];
  tags: string[];
  description?: string;
}

interface ShowTransferDialogProps {
  show: OutsourcedShow;
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (data: ShowTransferData, additionalFields: TransferShowAdditionalFields) => void;
  loading?: boolean;
}

const MATURITY_RATINGS = [
  { value: 'PG_13', label: '13+' },
  { value: 'RATED_16', label: '16+' },
  { value: 'RATED_R', label: '18+' },
];

export function ShowTransferDialog({
  show,
  isOpen,
  onClose,
  onSubmit,
  loading = false
}: ShowTransferDialogProps) {
  const [formData, setFormData] = useState<ShowTransferData>({
    showName: show.ogShowName || '',
    description: '',
    actorEthnicity: '',
    targetAudienceGender: '',
    maturityRating: '',
    sourceLanguage: '',
    subGenres: [],
    subTropes: [],
    tags: [],
  });

  const [tempInputs, setTempInputs] = useState({
    subGenres: '',
    subTropes: '',
    tags: ''
  });

  const handleInputChange = (field: keyof ShowTransferData, value: string) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleArrayInputChange = (field: 'subGenres' | 'subTropes' | 'tags', value: string) => {
    setTempInputs(prev => ({ ...prev, [field]: value }));
  };

  const handleAddItem = (field: 'subGenres' | 'subTropes' | 'tags') => {
    const value = tempInputs[field].trim();
    if (value && !formData[field].includes(value)) {
      setFormData(prev => ({
        ...prev,
        [field]: [...prev[field], value]
      }));
      setTempInputs(prev => ({ ...prev, [field]: '' }));
    }
  };

  const handleKeyPress = (field: 'subGenres' | 'subTropes' | 'tags', e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleAddItem(field);
    }
  };

  const handleRemoveItem = (field: 'subGenres' | 'subTropes' | 'tags', index: number) => {
    setFormData(prev => ({
      ...prev,
      [field]: prev[field].filter((_, i) => i !== index)
    }));
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    if (!formData.showName.trim() || !formData.description.trim()) {
      return;
    }

    const additionalFields: TransferShowAdditionalFields = {
      showName: formData.showName,
      actorEthnicity: formData.actorEthnicity || undefined,
      targetAudienceGender: formData.targetAudienceGender || undefined,
      maturityRating: formData.maturityRating || undefined,
      sourceLanguage: formData.sourceLanguage || undefined,
      subTropes: [...formData.subGenres, ...formData.subTropes],
      tags: formData.tags.length > 0 ? formData.tags : [],
      description: formData.description || undefined
    };

    onSubmit(formData, additionalFields);
  };

  if (!isOpen) return null;

  return (
    <>
      {/* Overlay */}
      <div
        className="fixed inset-0 bg-black/50 z-40 transition-opacity"
        onClick={onClose}
      />

      {/* Dialog */}
      <div className="fixed top-0 right-0 h-screen w-[600px] bg-background border-l shadow-lg z-50 transform transition-transform duration-300 translate-x-0">

        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b">
          <div>
            <h2 className="text-xl font-semibold">Transfer to Main Shows</h2>
            <p className="text-sm text-muted-foreground">Transfer "{show.ogShowName}" to main platform</p>
          </div>
          <Button variant="ghost" size="icon" onClick={onClose}>
            <X className="h-4 w-4" />
          </Button>
        </div>

        {/* Content */}
        <div className="flex flex-col" style={{height: 'calc(100vh - 89px)'}}>
          <div className="flex-1 overflow-y-auto p-6 space-y-6">

            {/* Existing Information */}
            <Card>
              <CardHeader>
                <CardTitle className="text-base">Existing Information</CardTitle>
                <p className="text-sm text-muted-foreground">Information from the outsourced show</p>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Source</Label>
                    <Input value={show.source || '-'} readOnly className="bg-muted" />
                  </div>
                  <div className="space-y-2">
                    <Label>Original Name</Label>
                    <Input value={show.ogShowName} readOnly className="bg-muted" />
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Episodes</Label>
                    <Input value={show.episodes.toString()} readOnly className="bg-muted" />
                  </div>
                  <div className="space-y-2">
                    <Label>Genre</Label>
                    <Input value={show.genre} readOnly className="bg-muted" />
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Trope</Label>
                    <Input value={show.trope} readOnly className="bg-muted" />
                  </div>
                  <div className="space-y-2">
                    <Label>Lead Character Gender</Label>
                    <Input value={show.leadCharacterGender} readOnly className="bg-muted" />
                  </div>
                </div>

                {(show.originCountry || show.dashreelsGrowthName) && (
                  <div className="grid grid-cols-2 gap-4">
                    {show.originCountry && (
                      <div className="space-y-2">
                        <Label>Origin Country</Label>
                        <Input value={show.originCountry} readOnly className="bg-muted" />
                      </div>
                    )}
                    {show.dashreelsGrowthName && (
                      <div className="space-y-2">
                        <Label>Dashreels Growth Name</Label>
                        <Input value={show.dashreelsGrowthName} readOnly className="bg-muted" />
                      </div>
                    )}
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Additional Information Required */}
            <Card>
              <CardHeader>
                <CardTitle className="text-base">Additional Information Required</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                {/* Show Name - Required */}
                <div className="space-y-2">
                  <Label htmlFor="showName">Platform Name *</Label>
                  <Input
                    id="showName"
                    value={formData.showName}
                    onChange={(e) => handleInputChange('showName', e.target.value)}
                    placeholder="Enter the show name for the main platform"
                    required
                  />
                </div>

                {/* Description - Required */}
                <div className="space-y-2">
                  <Label htmlFor="description">Show Description *</Label>
                  <textarea
                    id="description"
                    value={formData.description}
                    onChange={(e) => handleInputChange('description', e.target.value)}
                    placeholder="Enter a detailed description of the show"
                    rows={4}
                    className="flex w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 resize-none"
                    required
                  />
                </div>

                {/* Actor Ethnicity */}
                <div className="space-y-2">
                  <Label htmlFor="actorEthnicity">Actor Ethnicity</Label>
                  <Input
                    id="actorEthnicity"
                    value={formData.actorEthnicity}
                    onChange={(e) => handleInputChange('actorEthnicity', e.target.value)}
                    placeholder="Enter actor ethnicity"
                  />
                </div>

                <div className="grid grid-cols-2 gap-4">
                  {/* Target Audience Gender */}
                  <div className="space-y-2">
                    <Label htmlFor="targetAudienceGender">Target Audience Gender</Label>
                    <select
                      id="targetAudienceGender"
                      value={formData.targetAudienceGender}
                      onChange={(e) => handleInputChange('targetAudienceGender', e.target.value)}
                      className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
                    >
                      <option value="">Select gender</option>
                      {Object.values(CharacterGender).map(gender => (
                        <option key={gender} value={gender}>{gender}</option>
                      ))}
                    </select>
                  </div>

                  {/* Maturity Rating */}
                  <div className="space-y-2">
                    <Label htmlFor="maturityRating">Maturity Rating</Label>
                    <select
                      id="maturityRating"
                      value={formData.maturityRating}
                      onChange={(e) => handleInputChange('maturityRating', e.target.value)}
                      className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
                    >
                      <option value="">Select rating</option>
                      {MATURITY_RATINGS.map(rating => (
                        <option key={rating.value} value={rating.value}>{rating.label}</option>
                      ))}
                    </select>
                  </div>
                </div>

                {/* Source Language */}
                <div className="space-y-2">
                  <Label htmlFor="sourceLanguage">Source Language</Label>
                  <Input
                    id="sourceLanguage"
                    value={formData.sourceLanguage}
                    onChange={(e) => handleInputChange('sourceLanguage', e.target.value)}
                    placeholder="Enter source language"
                  />
                </div>

                {/* Sub Genres */}
                <div className="space-y-2">
                  <Label>Sub Genres</Label>
                  <div className="flex gap-2">
                    <Input
                      value={tempInputs.subGenres}
                      onChange={(e) => handleArrayInputChange('subGenres', e.target.value)}
                      onKeyDown={(e) => handleKeyPress('subGenres', e)}
                      placeholder="Type sub genre and press Enter"
                      className="flex-1"
                    />
                    <Button
                      type="button"
                      onClick={() => handleAddItem('subGenres')}
                      disabled={!tempInputs.subGenres.trim()}
                      size="sm"
                    >
                      <Plus className="h-4 w-4" />
                    </Button>
                  </div>
                  {formData.subGenres.length > 0 && (
                    <div className="flex flex-wrap gap-2">
                      {formData.subGenres.map((genre, index) => (
                        <Badge key={index} variant="secondary" className="bg-blue-100 text-blue-800">
                          {genre}
                          <Button
                            type="button"
                            variant="ghost"
                            size="sm"
                            onClick={() => handleRemoveItem('subGenres', index)}
                            className="h-4 w-4 p-0 ml-1 hover:bg-transparent"
                          >
                            <X className="h-3 w-3" />
                          </Button>
                        </Badge>
                      ))}
                    </div>
                  )}
                </div>

                {/* Sub Tropes */}
                <div className="space-y-2">
                  <Label>Sub Tropes</Label>
                  <div className="flex gap-2">
                    <Input
                      value={tempInputs.subTropes}
                      onChange={(e) => handleArrayInputChange('subTropes', e.target.value)}
                      onKeyDown={(e) => handleKeyPress('subTropes', e)}
                      placeholder="Type sub trope and press Enter"
                      className="flex-1"
                    />
                    <Button
                      type="button"
                      onClick={() => handleAddItem('subTropes')}
                      disabled={!tempInputs.subTropes.trim()}
                      size="sm"
                    >
                      <Plus className="h-4 w-4" />
                    </Button>
                  </div>
                  {formData.subTropes.length > 0 && (
                    <div className="flex flex-wrap gap-2">
                      {formData.subTropes.map((trope, index) => (
                        <Badge key={index} variant="secondary" className="bg-purple-100 text-purple-800">
                          {trope}
                          <Button
                            type="button"
                            variant="ghost"
                            size="sm"
                            onClick={() => handleRemoveItem('subTropes', index)}
                            className="h-4 w-4 p-0 ml-1 hover:bg-transparent"
                          >
                            <X className="h-3 w-3" />
                          </Button>
                        </Badge>
                      ))}
                    </div>
                  )}
                </div>

                {/* Tags */}
                <div className="space-y-2">
                  <Label>Tags</Label>
                  <div className="flex gap-2">
                    <Input
                      value={tempInputs.tags}
                      onChange={(e) => handleArrayInputChange('tags', e.target.value)}
                      onKeyDown={(e) => handleKeyPress('tags', e)}
                      placeholder="Type tag and press Enter"
                      className="flex-1"
                    />
                    <Button
                      type="button"
                      onClick={() => handleAddItem('tags')}
                      disabled={!tempInputs.tags.trim()}
                      size="sm"
                    >
                      <Plus className="h-4 w-4" />
                    </Button>
                  </div>
                  {formData.tags.length > 0 && (
                    <div className="flex flex-wrap gap-2">
                      {formData.tags.map((tag, index) => (
                        <Badge key={index} variant="secondary" className="bg-green-100 text-green-800">
                          {tag}
                          <Button
                            type="button"
                            variant="ghost"
                            size="sm"
                            onClick={() => handleRemoveItem('tags', index)}
                            className="h-4 w-4 p-0 ml-1 hover:bg-transparent"
                          >
                            <X className="h-3 w-3" />
                          </Button>
                        </Badge>
                      ))}
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>
          </div>
          
          {/* Fixed Footer */}
          <form onSubmit={handleSubmit} className="border-t p-6 bg-background flex-shrink-0">
            <div className="flex justify-between items-center">
              <Button type="button" variant="outline" onClick={onClose} disabled={loading}>
                Cancel
              </Button>
              <div className="flex gap-2">
                <Button
                    type="submit"
                    disabled={loading || !formData.showName.trim() || !formData.description.trim()}
                    className="bg-green-600 hover:bg-green-700"
                >
                  {loading ? 'Transferring...' : 'Confirm'}
                </Button>
              </div>
            </div>
          </form>
        </div>
      </div>
    </>
  );
}